import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import axios from 'axios';
import { forcedLifestyleMode } from '../config/appMode';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

interface User {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  role: string;
  userType: string;
  lifestyleMode?: string;
  healthGoals?: string[]; // Array of health goals (multiple goals support)
  subscriptionType: string;
  
  // Computed property for backward compatibility
  get healthGoal(): string | undefined;
}

interface AuthContextType {
  user: User | null;
  accessToken: string | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, firstName?: string, lastName?: string, userType?: string) => Promise<void>;
  logout: () => void;
  refreshUser: () => void;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Track if we're currently refreshing to avoid multiple simultaneous refresh calls
let isRefreshing = false;
let failedQueue: Array<{ resolve: (token: string) => void; reject: (error: any) => void }> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token!);
    }
  });
  failedQueue = [];
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Helper: Convert raw user data to User object with computed healthGoal.
  // În modul FITNESS (FII FIT), suprascriem `lifestyleMode` cu valoarea
  // forțată ('HEALTHY'), astfel încât toate componentele care decid pe baza
  // acestei valori (Navigation, Dashboard, etc.) să afișeze doar fluxul
  // sănătos/fitness, indiferent de ce este salvat în BD din vechiul cont
  // modul CHRONIC.
  const processUserData = (userData: any): User => {
    const forced = forcedLifestyleMode();
    return {
      ...userData,
      ...(forced ? { lifestyleMode: forced } : {}),
      // Add computed property for backward compatibility
      get healthGoal() {
        return this.healthGoals?.[0];
      }
    };
  };

  // Logout helper (can be called from interceptor)
  const performLogout = () => {
    setUser(null);
    setAccessToken(null);
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    // IMPORTANT: curăță și date cache-uite specifice user-ului, altfel
    // la login cu alt cont se afișează poza/avatarul contului vechi până
    // când se termină request-ul către /api/profile/photo.
    localStorage.removeItem('profilePhotoUrl');
    localStorage.removeItem('fcmToken');
  };

  // Load user from localStorage on mount + setup axios interceptor
  useEffect(() => {
    const storedToken = localStorage.getItem('accessToken');
    const storedUser = localStorage.getItem('user');

    if (storedToken && storedUser) {
      setAccessToken(storedToken);
      const userData = JSON.parse(storedUser);
      setUser(processUserData(userData));
    }
    setLoading(false);
  }, []);

  // Setup axios interceptor for automatic token refresh
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        // If error is 401 and we haven't already tried to refresh
        if (error.response?.status === 401 && !originalRequest._retry) {
          // Don't try to refresh if the failing request IS the refresh endpoint
          if (originalRequest.url?.includes('/api/auth/refresh')) {
            performLogout();
            window.location.href = '/login';
            return Promise.reject(error);
          }

          if (isRefreshing) {
            // If already refreshing, queue this request
            return new Promise((resolve, reject) => {
              failedQueue.push({ resolve, reject });
            }).then((token) => {
              originalRequest.headers['Authorization'] = `Bearer ${token}`;
              return axios(originalRequest);
            }).catch((err) => {
              return Promise.reject(err);
            });
          }

          originalRequest._retry = true;
          isRefreshing = true;

          const storedRefreshToken = localStorage.getItem('refreshToken');

          if (!storedRefreshToken) {
            // No refresh token available - logout
            isRefreshing = false;
            processQueue(error, null);
            performLogout();
            window.location.href = '/login';
            return Promise.reject(error);
          }

          try {
            // Try to refresh the token
            const refreshResponse = await axios.post(`${API_URL}/api/auth/refresh`, {
              refreshToken: storedRefreshToken,
            });

            const { accessToken: newAccessToken, refreshToken: newRefreshToken } = refreshResponse.data;

            // Update localStorage
            localStorage.setItem('accessToken', newAccessToken);
            if (newRefreshToken) {
              localStorage.setItem('refreshToken', newRefreshToken);
            }

            // Update state
            setAccessToken(newAccessToken);

            // Update axios default header
            axios.defaults.headers.common['Authorization'] = `Bearer ${newAccessToken}`;

            // Process queued requests
            processQueue(null, newAccessToken);

            // Retry original request with new token
            originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`;
            return axios(originalRequest);
          } catch (refreshError) {
            // Refresh failed - logout user
            processQueue(refreshError, null);
            performLogout();
            window.location.href = '/login';
            return Promise.reject(refreshError);
          } finally {
            isRefreshing = false;
          }
        }

        return Promise.reject(error);
      }
    );

    // Cleanup interceptor on unmount
    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, []);

  // Register function
  const register = async (email: string, password: string, firstName?: string, lastName?: string, userType?: string) => {
    try {
      const response = await axios.post(`${API_URL}/api/auth/register`, {
        email,
        password,
        firstName,
        lastName,
        userType: userType || 'PATIENT',
      });

      const { user: userData, accessToken: token, refreshToken } = response.data;

      // Process user data with computed healthGoal
      const processedUser = processUserData(userData);

      // Save to localStorage FIRST
      localStorage.setItem('accessToken', token);
      localStorage.setItem('refreshToken', refreshToken);
      localStorage.setItem('user', JSON.stringify(userData));

      // Then update state
      setAccessToken(token);
      setUser(processedUser);
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Eroare la înregistrare');
    }
  };

  // Login function
  const login = async (email: string, password: string) => {
    console.log('🔐 Login attempt starting...');
    console.log('API_URL:', API_URL);
    console.log('Request URL:', `${API_URL}/api/auth/login`);
    console.log('Request data:', { email, passwordLength: password.length });
    
    try {
      const response = await axios.post(
        `${API_URL}/api/auth/login`,
        {
          email,
          password,
        },
        {
          headers: {
            'Content-Type': 'application/json',
          },
          timeout: 30000, // 30 seconds timeout
        }
      );

      console.log('✅ Login response received:', response.status);
      const { user: userData, accessToken: token, refreshToken } = response.data;

      // Process user data with computed healthGoal
      const processedUser = processUserData(userData);

      // IMPORTANT: Save to localStorage FIRST before updating state
      // This ensures the token is available immediately for subsequent requests
      localStorage.setItem('accessToken', token);
      localStorage.setItem('refreshToken', refreshToken);
      localStorage.setItem('user', JSON.stringify(userData));

      // Then update state (this triggers re-renders)
      setAccessToken(token);
      setUser(processedUser);
      console.log('✅ Login successful!');
    } catch (error: any) {
      console.error('❌ Login error details:');
      console.error('Error object:', error);
      console.error('Error message:', error.message);
      console.error('Error code:', error.code);
      console.error('Response status:', error.response?.status);
      console.error('Response data:', error.response?.data);
      console.error('Request config:', error.config);
      
      // Better error messages based on error type
      if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
        throw new Error('Eroare de rețea. Verifică conexiunea la internet și încearcă din nou.');
      } else if (error.code === 'ECONNABORTED') {
        throw new Error('Request timeout. Server-ul nu răspunde.');
      } else if (error.response?.status === 401) {
        throw new Error('Email sau parolă incorectă.');
      } else if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      } else {
        throw new Error('Eroare la autentificare. Verifică datele și încearcă din nou.');
      }
    }
  };

  // Logout function
  const logout = () => {
    performLogout();
  };

  // Refresh user from localStorage (after external updates like saving health goals)
  const refreshUser = () => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      const userData = JSON.parse(storedUser);
      setUser(processUserData(userData));
    }
  };

  const value = {
    user,
    accessToken,
    loading,
    login,
    register,
    logout,
    refreshUser,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// Custom hook to use auth context
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
